import { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { format, addMonths } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Layout } from "@/frontend/components/layout/Layout";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { Textarea } from "@/frontend/components/ui/textarea";
import { Checkbox } from "@/frontend/components/ui/checkbox";
import { Calendar } from "@/frontend/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/frontend/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/frontend/components/ui/popover";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/frontend/components/ui/breadcrumb";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/frontend/components/ui/tabs";
import { cn } from "@/frontend/lib/utils";
import { useResidences } from "@/backend/hooks/useResidences";
import { useCreateBooking } from "@/backend/hooks/useCreateBooking";
import { usePaymentSettings } from "@/backend/hooks/usePaymentSettings";
import { useRoomTypes } from "@/backend/hooks/useRoomTypes";
import { useAvailableRooms } from "@/backend/hooks/useAvailableRooms";
import { useRoomTypeOccupancy, findFirstAvailableUnit, intersectOccupiedRanges, type OccupiedRange } from "@/backend/hooks/useRoomTypeOccupancy";
import { AvailableRoomsList } from "@/frontend/components/booking/AvailableRoomsList";
import { GuestFields } from "@/frontend/components/booking/GuestFields";
import { validateGuest, type GuestFieldErrors } from "@/shared/constants/guestForm";
import { BookingFormData, GuestInfo, AvailableRoom } from "@/shared/types";
import { monthsInclusiveSnapped, snapCheckIn, snapCheckOut, earliestCheckOutMonth, defaultCheckOut } from "@/shared/utils/monthRange";
import { BookingSuccessDialog, BookingErrorDialog } from "@/frontend/components/booking/BookingDialogs";

// Stable empty defaults so the `?? …` fallbacks below don't change identity
// on every render and trip the exhaustive-deps lint on the memoized callbacks
// and modifiers that consume them.
const EMPTY_OCCUPIED_RANGES: OccupiedRange[] = [];
const EMPTY_UNIT_RANGES: Record<string, OccupiedRange[]> = {};

const Book = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedResidence = searchParams.get("residence");
  const preselectedRoomType = searchParams.get("roomType");
  const preselectedCheckInParam = searchParams.get("checkIn");
  const preselectedCheckIn = (() => {
    if (!preselectedCheckInParam) return undefined;
    const d = new Date(preselectedCheckInParam);
    return isNaN(d.getTime()) ? undefined : d;
  })();

  const [bookingId, setBookingId] = useState<string>("");
  const [bookingReference, setBookingReference] = useState<string>("");
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [selectedAvailableRoom, setSelectedAvailableRoom] = useState<AvailableRoom | null>(null);
  const [roomUnavailableNotice, setRoomUnavailableNotice] = useState(false);
  const [formData, setFormData] = useState<BookingFormData>({
    residenceId: preselectedResidence || "",
    roomTypeId: preselectedRoomType || "",
    checkIn: preselectedCheckIn,
    checkOut: undefined,
    numberOfOccupants: 1,
    guests: [{ name: "", email: "", phone: "" }],
    notes: "",
    termsAccepted: false,
    privacyAccepted: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof BookingFormData, string>>>({});
  const [activeGuestTab, setActiveGuestTab] = useState("0");
  const [guestErrors, setGuestErrors] = useState<GuestFieldErrors[]>([{}]);

  const { data: residences = [] } = useResidences(true);
  const { data: availableRooms = [], isFetching: isLoadingRooms } = useAvailableRooms(formData.checkIn);
  // Used only to resolve the typology of a URL-preselected room before
  // `useAvailableRooms` returns — so the move-in calendar can already disable
  // dates that overlap the typology's occupancy.
  const { data: residenceRoomTypes = [] } = useRoomTypes(formData.residenceId);

  // Typology-wide occupancy: a date is only disabled when ALL units in the
  // typology are taken. Prefer the AvailableRoom's `unitRoomTypeIds` once it's
  // bound; otherwise (URL pre-selection, before the first date pick) derive
  // the typology from the residence's room types.
  const typologyUnitIds: string[] | undefined = selectedAvailableRoom
    ? selectedAvailableRoom.unitRoomTypeIds
    : formData.roomTypeId
      ? (() => {
          const base = residenceRoomTypes.find((rt) => rt.id === formData.roomTypeId);
          const key = base?.typologyKey?.trim();
          if (!base) return undefined;
          if (!key) return [base.id];
          return residenceRoomTypes
            .filter((rt) => rt.typologyKey?.trim() === key)
            .map((rt) => rt.id);
        })()
      : undefined;
  const { data: occupancyData } = useRoomTypeOccupancy(typologyUnitIds);

  // Occupancy source: authenticated users get it from the direct query
  // (useRoomTypeOccupancy). Guests can't read bookings/contracts, so they get
  // per-unit occupied ranges from the getAvailableRooms Cloud Function, carried
  // on the selected AvailableRoom. This keeps the date-picker greying + the
  // submit-time unit check working for guests without a direct read.
  const serverOccupancy = useMemo(() => {
    const unitOccupied = selectedAvailableRoom?.unitOccupied;
    if (!unitOccupied) return undefined;
    const unitRanges: Record<string, OccupiedRange[]> = {};
    for (const [unitId, ranges] of Object.entries(unitOccupied)) {
      unitRanges[unitId] = ranges.map((r) => ({
        start: new Date(r.start),
        end: new Date(r.end),
        source: "booking" as const,
      }));
    }
    const ids = Object.keys(unitRanges);
    const occupiedRanges = intersectOccupiedRanges(ids.map((id) => unitRanges[id]));
    return { occupiedRanges, unitRanges };
  }, [selectedAvailableRoom?.unitOccupied]);

  const occupiedRanges =
    occupancyData?.occupiedRanges ?? serverOccupancy?.occupiedRanges ?? EMPTY_OCCUPIED_RANGES;
  const unitRanges = occupancyData?.unitRanges ?? serverOccupancy?.unitRanges ?? EMPTY_UNIT_RANGES;
  const createBooking = useCreateBooking();
  const { data: paymentSettings } = usePaymentSettings();

  const selectedResidence = useMemo(
    () => residences.find((r) => r.id === formData.residenceId),
    [residences, formData.residenceId]
  );

  // Prefer the bound AvailableRoom's minStay; fall back to the residence's
  // room types when a URL pre-selection hasn't been bound yet.
  const minStay =
    selectedAvailableRoom?.minStay ??
    residenceRoomTypes.find((rt) => rt.id === formData.roomTypeId)?.minStay ??
    1;

  // Bind a room to the form state. Does NOT touch occupants/guests — the
  // reconciliation path (date-change refresh) needs to preserve the user's
  // student-info progress, while the explicit click path (handleRoomSelect)
  // resets those fields itself when the user picks a different room.
  const applyRoomSelection = useCallback((room: AvailableRoom) => {
    setSelectedAvailableRoom(room);
    setRoomUnavailableNotice(false);
    setFormData((prev) => ({
      ...prev,
      residenceId: room.residenceId,
      roomTypeId: room.roomTypeId,
      checkOut: prev.checkIn ? defaultCheckOut(prev.checkIn, room.minStay) : undefined,
    }));
  }, []);

  // Whenever the availability list refreshes with a `formData.roomTypeId` to
  // reconcile, either re-bind to the freshly returned room (preserving form
  // state) or surface the inline banner.
  useEffect(() => {
    if (!formData.checkIn) return;
    if (isLoadingRooms) return;
    if (!formData.roomTypeId) return;

    const match = availableRooms.find(
      (r) =>
        r.roomTypeId === formData.roomTypeId ||
        r.unitRoomTypeIds.includes(formData.roomTypeId!)
    );

    if (match) {
      // Refresh the in-state room ref (price / minStay / unitRoomTypeIds may
      // have changed for the new date) and recompute checkOut if it's empty.
      // Skip if we're already bound to this exact representative to avoid an
      // infinite update loop.
      if (selectedAvailableRoom?.roomTypeId === match.roomTypeId) return;
      applyRoomSelection(match);
    } else if (selectedAvailableRoom || formData.roomTypeId) {
      // No room in the list matches the previously selected typology.
      setSelectedAvailableRoom(null);
      setRoomUnavailableNotice(true);
      setFormData((prev) => ({
        ...prev,
        residenceId: "",
        roomTypeId: "",
      }));
    }
  }, [
    availableRooms,
    isLoadingRooms,
    formData.checkIn,
    formData.roomTypeId,
    selectedAvailableRoom,
    applyRoomSelection,
  ]);

  const isCheckInDisabled = useCallback(
    (date: Date) => {
      if (date < new Date()) return true;
      // Bookings persist as whole months, so test the SNAPPED claim window
      // (1st of month -> exclusive end after the min-stay months), not the
      // student's exact day — what we reserve is what we must check.
      const claimStart = snapCheckIn(date);
      const claimEndExclusive = addMonths(earliestCheckOutMonth(date, minStay), 1);
      return occupiedRanges.some(r => claimStart < r.end && claimEndExclusive > r.start);
    },
    [occupiedRanges, minStay]
  );

  const isCheckOutDisabled = useCallback(
    (date: Date) => {
      if (!formData.checkIn) return true;
      // Student picks an exact day; billing snaps to whole months. The stay
      // must cover at least `minStay` whole calendar months once snapped.
      if (monthsInclusiveSnapped(formData.checkIn, date) < minStay) return true;
      // Snapped claim must not cross into a later occupied range.
      const claimStart = snapCheckIn(formData.checkIn);
      const claimEnd = snapCheckOut(date);
      return occupiedRanges.some(r => r.start > claimStart && claimEnd >= r.start);
    },
    [occupiedRanges, formData.checkIn, minStay]
  );

  const occupiedModifiers = useMemo(
    () => ({ occupied: occupiedRanges.map((r: OccupiedRange) => ({ from: r.start, to: r.end })) }),
    [occupiedRanges]
  );
  const occupiedModifiersClassNames = { occupied: 'opacity-40 line-through' };

  const handleUpdateGuest = (index: number, field: keyof GuestInfo, value: string) => {
    setFormData((prev) => {
      const guests = [...prev.guests];
      // value is always a string from the inputs/selects; cast covers the
      // union-typed fields (documentType, occupation).
      guests[index] = { ...guests[index], [field]: value } as GuestInfo;
      return { ...prev, guests };
    });
  };

  const resizeGuests = (prev: BookingFormData, count: number): GuestInfo[] => {
    if (count > prev.guests.length) {
      return [...prev.guests, ...Array(count - prev.guests.length).fill({ name: "", email: "", phone: "" })];
    }
    return prev.guests.slice(0, count);
  };

  const handleRoomSelect = (room: AvailableRoom) => {
    if (selectedAvailableRoom?.roomTypeId === room.roomTypeId) {
      setSelectedAvailableRoom(null);
      setActiveGuestTab("0");
      setFormData((prev) => ({
        ...prev,
        residenceId: "",
        roomTypeId: "",
        numberOfOccupants: 1,
        guests: [{ name: "", email: "", phone: "" }],
        checkOut: undefined,
      }));
      return;
    }
    // Explicit user pick of a (different) room: reset occupants & guests, then
    // bind. applyRoomSelection itself no longer touches those fields.
    setActiveGuestTab("0");
    setFormData((prev) => ({
      ...prev,
      numberOfOccupants: 1,
      guests: [{ name: "", email: "", phone: "" }],
    }));
    applyRoomSelection(room);
  };

  const handleCheckInSelect = (date: Date | undefined) => {
    // Preserve the room selection and student-info progress. The reconciliation
    // effect re-validates the room against the freshly fetched availability
    // list. Recompute check-out from the new move-in + minStay so the user
    // doesn't have to re-pick it — mirroring the first-selection behaviour.
    setRoomUnavailableNotice(false);
    setFormData((prev) => ({
      ...prev,
      checkIn: date,
      checkOut: date && selectedAvailableRoom ? defaultCheckOut(date, selectedAvailableRoom.minStay) : undefined,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof BookingFormData, string>> = {};

    if (!formData.roomTypeId) {
      newErrors.roomTypeId = "Please select a room from the list above";
    }

    if (!formData.checkIn) {
      newErrors.checkIn = "Please select a move-in date";
    }
    if (!formData.checkOut) {
      newErrors.checkOut = "Please select a move-out date";
    }

    if (formData.checkIn && formData.checkOut) {
      // Billing snaps exact dates to whole months; validate that span.
      const monthsDiff = monthsInclusiveSnapped(formData.checkIn, formData.checkOut);
      if (monthsDiff < minStay) {
        newErrors.checkOut = `Minimum stay is ${minStay} month${minStay > 1 ? 's' : ''}`;
      }
    }

    // Per-guest rules live in shared/constants/guestForm so the student
    // "complete your details" dialog validates identically.
    const newGuestErrors: GuestFieldErrors[] = formData.guests.map(validateGuest);
    setGuestErrors(newGuestErrors);
    const firstErrorIdx = newGuestErrors.findIndex((e) => Object.keys(e).length > 0);
    if (firstErrorIdx !== -1) setActiveGuestTab(String(firstErrorIdx));

    if (!formData.termsAccepted) {
      newErrors.termsAccepted = "You must accept the terms and conditions";
    }

    if (!formData.privacyAccepted) {
      newErrors.privacyAccepted = "You must accept the privacy policy";
    }

    setErrors(newErrors);
    const hasGuestErrors = newGuestErrors.some((e) => Object.keys(e).length > 0);
    return Object.keys(newErrors).length === 0 && !hasGuestErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      // Defense-in-depth: re-verify against the freshest occupancy snapshot
      // before submitting, including single-unit typologies. findFirstAvailableUnit
      // returns the first unit free for the window or undefined if none is.
      let finalRoomTypeId = formData.roomTypeId;
      if (typologyUnitIds && formData.checkIn && formData.checkOut) {
        // Check the SNAPPED claim window — the booking is persisted as whole
        // months, so the unit must be free for the whole-month range, not just
        // the student's exact days. Exclusive end = 1st of the month after checkout.
        const free = findFirstAvailableUnit(
          typologyUnitIds,
          unitRanges,
          snapCheckIn(formData.checkIn),
          addMonths(snapCheckIn(formData.checkOut), 1),
        );
        if (!free) {
          setErrorMessage(
            "This room is no longer available for the dates you picked. It may have just been booked by someone else. Please choose a different room or adjust your dates."
          );
          setShowErrorDialog(true);
          return;
        }
        finalRoomTypeId = free;
      }
      const result = await createBooking.mutateAsync({
        ...formData,
        roomTypeId: finalRoomTypeId,
        // Send the whole typology so the server picks/validates a free unit.
        unitRoomTypeIds: typologyUnitIds,
      });
      setBookingId(result.id);
      setBookingReference(result.reference);
      setShowSuccessDialog(true);
    } catch (error) {
      setErrorMessage(undefined);
      setShowErrorDialog(true);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    navigate('/');
  };

  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
    setErrorMessage(undefined);
  };

  const handleRetry = () => {
    // Form is still populated, user can just resubmit
  };

  const showPersonalInfo = !!selectedAvailableRoom;

  return (
    <Layout>
      <div className="pt-24 md:pt-28 min-h-screen bg-muted/30">
        <div className="container-narrow section-padding">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-8">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Book</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
              Book Your Stay
            </h1>
            <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
              Select your move-in date to see available rooms.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
            <div className="bg-card rounded-xl p-6 md:p-8 shadow-sm space-y-8">

              {/* Step 1: Move-in date */}
              <div>
                <h2 className="text-lg font-heading font-semibold text-foreground mb-2">
                  When would you like to move in?
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Select your desired move-in date to see available rooms.
                </p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full sm:w-auto justify-start text-left font-normal",
                        !formData.checkIn && "text-muted-foreground",
                        errors.checkIn && "border-destructive"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.checkIn ? format(formData.checkIn, "PPP") : "Pick a move-in date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.checkIn}
                      onSelect={handleCheckInSelect}
                      disabled={typologyUnitIds ? isCheckInDisabled : (date) => date < new Date()}
                      modifiers={typologyUnitIds ? occupiedModifiers : undefined}
                      modifiersClassNames={typologyUnitIds ? occupiedModifiersClassNames : undefined}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                {errors.checkIn && (
                  <p className="text-sm text-destructive mt-1">{errors.checkIn}</p>
                )}
              </div>

              {/* Step 2: Available rooms (shown after date is selected) */}
              {formData.checkIn && (
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground mb-2">
                    Available rooms
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Select the room you'd like to book.
                  </p>
                  {roomUnavailableNotice && (
                    <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-foreground">
                      This room isn't available for the new date. Please choose another below.
                    </div>
                  )}
                  <AvailableRoomsList
                    rooms={availableRooms}
                    selectedRoomTypeId={formData.roomTypeId}
                    selectedOccupants={formData.numberOfOccupants > 1 ? formData.numberOfOccupants : undefined}
                    onSelect={handleRoomSelect}
                    isLoading={isLoadingRooms && availableRooms.length === 0}
                  />
                  {errors.roomTypeId && !isLoadingRooms && (
                    <p className="text-sm text-destructive mt-2">{errors.roomTypeId}</p>
                  )}
                </div>
              )}

              {/* Step 3: Number of occupants (shown after room is selected) */}
              {selectedAvailableRoom && (
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground mb-2">
                    Number of occupants
                  </h2>
                  <div className="space-y-3">
                    <Select
                      value={String(formData.numberOfOccupants)}
                      onValueChange={(value) => {
                        const count = Number(value);
                        setActiveGuestTab("0");
                        setFormData((prev) => ({ ...prev, numberOfOccupants: count, guests: resizeGuests(prev, count) }));
                      }}
                    >
                      <SelectTrigger className="w-full sm:w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: selectedAvailableRoom.maxOccupancy }, (_, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)}>
                            {i + 1} {i === 0 ? "student" : "students"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm font-semibold text-secondary">
                      €{selectedAvailableRoom.priceByOccupancy[formData.numberOfOccupants - 1]}
                      <span className="text-sm font-normal text-muted-foreground">/month</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Step 4: Move-out date (shown after room is selected) */}
              {selectedAvailableRoom && formData.checkIn && (
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground mb-2">
                    Move-out date
                  </h2>
                  <div className="space-y-3">
                    <div className="rounded-lg bg-muted/50 border border-border px-4 py-3 text-sm text-muted-foreground">
                      Earliest move-out:{" "}
                      <span className="font-medium text-foreground">
                        {format(earliestCheckOutMonth(formData.checkIn, selectedAvailableRoom.minStay), "MMMM yyyy")}
                      </span>
                      {" "}({selectedAvailableRoom.minStay} month{selectedAvailableRoom.minStay > 1 ? 's' : ''} minimum stay — any day in that month works)
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full sm:w-auto justify-start text-left font-normal",
                            !formData.checkOut && "text-muted-foreground",
                            errors.checkOut && "border-destructive"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.checkOut ? format(formData.checkOut, "PPP") : "Pick a move-out date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.checkOut}
                          defaultMonth={earliestCheckOutMonth(formData.checkIn, selectedAvailableRoom.minStay)}
                          onSelect={(date) =>
                            setFormData((prev) => ({ ...prev, checkOut: date ?? undefined }))
                          }
                          disabled={isCheckOutDisabled}
                          modifiers={occupiedModifiers}
                          modifiersClassNames={occupiedModifiersClassNames}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    {errors.checkOut && (
                      <p className="text-sm text-destructive mt-1">{errors.checkOut}</p>
                    )}
                    {formData.checkIn && formData.checkOut && (() => {
                        const billStart = snapCheckIn(formData.checkIn);
                        const billEnd = snapCheckOut(formData.checkOut);
                        const months = monthsInclusiveSnapped(formData.checkIn, formData.checkOut);
                        return (
                          <div className="rounded-lg border-2 border-primary/30 bg-primary/5 px-4 py-3">
                            <p className="text-sm font-semibold text-primary">
                              You pay {months} month{months > 1 ? "s" : ""} ({format(billStart, "MMM")}–{format(billEnd, "MMM")})
                            </p>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              Billing period: {format(billStart, "d MMM yyyy")} → {format(billEnd, "d MMM yyyy")} — rent is charged per full calendar month, regardless of your exact arrival and departure days.
                            </p>
                          </div>
                        );
                    })()}
                  </div>
                </div>
              )}

              {/* ── STUDENT INFO ── */}
              {showPersonalInfo && (
                <div>
                  <h2 className="text-lg font-heading font-semibold text-foreground mb-6">
                    Student Information
                  </h2>
                  <div className="space-y-5">
                    {formData.guests.length <= 3 ? (
                      <Tabs value={activeGuestTab} onValueChange={setActiveGuestTab}>
                        <TabsList className="w-full">
                          {formData.guests.map((_, i) => (
                            <TabsTrigger
                              key={i}
                              value={String(i)}
                              className={cn(
                                "flex-1",
                                guestErrors[i] && Object.keys(guestErrors[i]).length > 0 && "text-destructive"
                              )}
                            >
                              Student {i + 1}
                            </TabsTrigger>
                          ))}
                        </TabsList>

                        {formData.guests.map((guest, i) => (
                          <TabsContent key={i} value={String(i)} className="space-y-4 mt-4">
                            <GuestFields
                              guest={guest}
                              index={i}
                              errors={guestErrors[i]}
                              onUpdate={(field, value) => handleUpdateGuest(i, field, value)}
                            />
                          </TabsContent>
                        ))}
                      </Tabs>
                    ) : (
                      <div className="space-y-4">
                        <Select value={activeGuestTab} onValueChange={setActiveGuestTab}>
                          <SelectTrigger className={cn(
                            "w-full",
                            guestErrors[Number(activeGuestTab)] && Object.keys(guestErrors[Number(activeGuestTab)]).length > 0 && "border-destructive"
                          )}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {formData.guests.map((_, i) => (
                              <SelectItem key={i} value={String(i)}>
                                <span className={cn(
                                  guestErrors[i] && Object.keys(guestErrors[i]).length > 0 && "text-destructive"
                                )}>
                                  Student {i + 1}
                                  {guestErrors[i] && Object.keys(guestErrors[i]).length > 0 && " ⚠"}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {formData.guests.map((guest, i) => (
                          Number(activeGuestTab) === i && (
                            <GuestFields
                              key={i}
                              guest={guest}
                              index={i}
                              errors={guestErrors[i]}
                              onUpdate={(field, value) => handleUpdateGuest(i, field, value)}
                            />
                          )
                        ))}
                      </div>
                    )}

                    <div>
                      <Label htmlFor="notes">Message / Notes (optional)</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, notes: e.target.value }))
                        }
                        placeholder="Any special requests or questions?"
                        rows={4}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ── TERMS + SUBMIT ── */}
              {showPersonalInfo && (
                <>
                  <div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="terms"
                        checked={formData.termsAccepted}
                        onCheckedChange={(checked) =>
                          setFormData((prev) => ({ ...prev, termsAccepted: checked === true }))
                        }
                        className={cn(errors.termsAccepted && "border-destructive")}
                      />
                      <Label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                        I agree to the{" "}
                        <Link to="/terms" className="text-secondary hover:underline">
                          Terms & Conditions
                        </Link>
                      </Label>
                    </div>
                    {errors.termsAccepted && (
                      <p className="text-sm text-destructive mt-1">{errors.termsAccepted}</p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="privacy"
                        checked={formData.privacyAccepted}
                        onCheckedChange={(checked) =>
                          setFormData((prev) => ({ ...prev, privacyAccepted: checked === true }))
                        }
                        className={cn(errors.privacyAccepted && "border-destructive")}
                      />
                      <Label htmlFor="privacy" className="text-sm leading-relaxed cursor-pointer">
                        I have read and understood the{" "}
                        <Link to="/privacy" className="text-secondary hover:underline">
                          Privacy Policy
                        </Link>{" "}
                        and agree to the processing of my personal data as described therein.
                      </Label>
                    </div>
                    {errors.privacyAccepted && (
                      <p className="text-sm text-destructive mt-1">{errors.privacyAccepted}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    variant="coral"
                    size="lg"
                    className="w-full"
                    disabled={createBooking.isPending}
                  >
                    {createBooking.isPending ? "Submitting..." : "Submit Booking Request"}
                  </Button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Success Dialog */}
      <BookingSuccessDialog
        open={showSuccessDialog}
        onClose={handleSuccessDialogClose}
        bookingReference={bookingReference || bookingId}
        bookingData={formData}
        residenceName={selectedResidence?.name ?? selectedAvailableRoom?.residenceName}
        roomTypeName={selectedAvailableRoom?.roomTypeName}
        amount={
          selectedAvailableRoom
            ? selectedAvailableRoom.priceByOccupancy[formData.numberOfOccupants - 1] ?? null
            : null
        }
        paymentSettings={paymentSettings}
      />

      {/* Error Dialog */}
      <BookingErrorDialog
        open={showErrorDialog}
        onClose={handleErrorDialogClose}
        onRetry={handleRetry}
        errorMessage={errorMessage}
      />
    </Layout>
  );
};

export default Book;
